import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { usePlayerStore } from '../../stores/usePlayerStore'
import { useProductionStore } from '../../stores/useProductionStore'
import { useMarketStore } from '../../stores/useMarketStore'
import { Card } from '../ui/Card'

export const Dashboard: React.FC = () => {
  const { t } = useTranslation()
  const { player, loadPlayer } = usePlayerStore()
  const { products, loadProducts } = useProductionStore()
  const { shops, loadShops } = useMarketStore()

  useEffect(() => {
    // Load initial data
    loadProducts()
    loadShops()
    
    // Load player data if we have a user
    if (player?.id) {
      loadPlayer(player.id)
    }
  }, [player?.id])

  return (
    <div className="space-y-6">
        {/* Welcome Section */}
        <Card title={`🏢 ${t('dashboard.title')}`} subtitle={t('dashboard.subtitle')}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="font-semibold text-green-800 mb-2">{t('dashboard.cashBalance')}</h3>
              <p className="text-3xl font-bold text-green-600">
                {player?.cash?.toLocaleString() || '1,000'} 💰
              </p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2">{t('dashboard.playerLevel')}</h3>
              <p className="text-3xl font-bold text-blue-600">
                {player?.level || 1} ⭐
              </p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="font-semibold text-purple-800 mb-2">{t('dashboard.experience')}</h3>
              <p className="text-3xl font-bold text-purple-600">
                {player?.experience || 0} 🎯
              </p>
            </div>
          </div>
        </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title={`⚙️ ${t('dashboard.production')}`} subtitle={t('dashboard.productionSubtitle')}>
          <div className="space-y-3">
            {products.slice(0, 3).map((product) => (
              <div key={product.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium">{product.name}</h4>
                  <p className="text-sm text-gray-600">{product.base_price} {t('common.price')}</p>
                </div>
                <button className="btn-primary text-sm">
                  {t('dashboard.produce')}
                </button>
              </div>
            ))}
            <div className="text-center">
              <Link to="/production" className="btn-secondary">
                {t('dashboard.viewAll')} {t('navigation.inventory')}
              </Link>
            </div>
          </div>
        </Card>

        <Card title={`🏪 ${t('navigation.market')}`} subtitle={t('dashboard.marketSubtitle')}>
          <div className="space-y-3">
            {shops.slice(0, 3).map((shop) => (
              <div key={shop.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium">{shop.name}</h4>
                  <p className="text-sm text-gray-600 capitalize">{shop.type}</p>
                </div>
                <button className="btn-primary text-sm">
                  {t('dashboard.sell')}
                </button>
              </div>
            ))}
            <div className="text-center">
              <Link to="/market" className="btn-secondary">
                {t('dashboard.viewAll')} {t('navigation.market')}
              </Link>
            </div>
          </div>
        </Card>
      </div>

      {/* Brand & Copyright Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title={`🏷️ ${t('dashboard.brands')}`} subtitle={t('dashboard.brandsSubtitle')}>
          <div className="space-y-3">
            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center ml-3">
                <span className="text-blue-600">🏷️</span>
              </div>
              <div>
                <h4 className="font-medium">{t('dashboard.goldenBrand')}</h4>
                <p className="text-sm text-gray-600">{t('dashboard.reputation')}: 85%</p>
              </div>
              <div className="mr-auto">
                <Link to="/brands" className="btn-primary text-sm">
                  {t('dashboard.manage')}
                </Link>
              </div>
            </div>
            <div className="text-center">
              <Link to="/brands" className="btn-secondary">
                {t('dashboard.viewAll')} {t('navigation.brands')}
              </Link>
            </div>
          </div>
        </Card>

        <Card title={`🛡️ ${t('navigation.copyright')}`} subtitle={t('dashboard.copyrightSubtitle')}>
          <div className="space-y-3">
            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center ml-3">
                <span className="text-green-600">✅</span>
              </div>
              <div>
                <h4 className="font-medium">{t('dashboard.barbariBread')}</h4>
                <p className="text-sm text-gray-600">{t('common.status')}: {t('productRequests.approved')}</p>
              </div>
              <div className="mr-auto">
                <Link to="/copyright" className="btn-primary text-sm">
                  {t('dashboard.manage')}
                </Link>
              </div>
            </div>
            <div className="text-center">
              <Link to="/copyright" className="btn-secondary">
                {t('dashboard.viewAll')} {t('dashboard.requests')}
              </Link>
            </div>
          </div>
        </Card>
      </div>

      {/* Employees & Machinery Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title={`👥 ${t('dashboard.employees')}`} subtitle={t('dashboard.employeesSubtitle')}>
          <div className="space-y-3">
            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center ml-3">
                <span className="text-purple-600">👥</span>
              </div>
              <div>
                <h4 className="font-medium">{t('dashboard.ahmadMohammadi')}</h4>
                <p className="text-sm text-gray-600">{t('dashboard.production')} • {t('dashboard.level')} 3</p>
              </div>
              <div className="mr-auto">
                <Link to="/employees" className="btn-primary text-sm">
                  {t('dashboard.manage')}
                </Link>
              </div>
            </div>
            <div className="text-center">
              <Link to="/employees" className="btn-secondary">
                {t('dashboard.viewAll')} {t('navigation.employees')}
              </Link>
            </div>
          </div>
        </Card>

        <Card title={`🔧 ${t('navigation.machinery')}`} subtitle={t('dashboard.machinerySubtitle')}>
          <div className="space-y-3">
            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center ml-3">
                <span className="text-orange-600">🔧</span>
              </div>
              <div>
                <h4 className="font-medium">{t('dashboard.breadMachine')}</h4>
                <p className="text-sm text-gray-600">{t('common.status')}: {t('dashboard.excellent')}</p>
              </div>
              <div className="mr-auto">
                <Link to="/machinery" className="btn-primary text-sm">
                  {t('dashboard.manage')}
                </Link>
              </div>
            </div>
            <div className="text-center">
              <Link to="/machinery" className="btn-secondary">
                {t('dashboard.viewAll')} {t('navigation.machinery')}
              </Link>
            </div>
          </div>
        </Card>
      </div>

      {/* AI Request Section */}
      <Card title={`🤖 ${t('navigation.productRequests')}`} subtitle={t('dashboard.aiRequestsSubtitle')}>
        <div className="space-y-3">
          <div className="flex items-center p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-purple-600">🤖</span>
            </div>
            <div>
              <h4 className="font-medium">{t('dashboard.specialBarbariBread')}</h4>
              <p className="text-sm text-gray-600">{t('common.status')}: {t('productRequests.approved')}</p>
            </div>
            <div className="mr-auto">
              <Link to="/product-requests" className="btn-primary text-sm">
                {t('dashboard.manage')}
              </Link>
            </div>
          </div>
          <div className="text-center">
            <Link to="/product-requests" className="btn-secondary">
              {t('dashboard.viewAll')} {t('dashboard.requests')}
            </Link>
          </div>
        </div>
      </Card>

      {/* Recent Activity */}
      <Card title={`📊 ${t('dashboard.recentActivity')}`} subtitle={t('dashboard.recentActivitySubtitle')}>
        <div className="space-y-3">
          <div className="flex items-center p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-green-600">💰</span>
            </div>
            <div>
              <h4 className="font-medium">{t('dashboard.gameStart')}</h4>
              <p className="text-sm text-gray-600">{t('dashboard.accountCreated')}</p>
            </div>
            <div className="text-sm text-gray-500 mr-auto">
              {t('dashboard.now')}
            </div>
          </div>
          
          <div className="flex items-center p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-blue-600">🎯</span>
            </div>
            <div>
              <h4 className="font-medium">{t('dashboard.level')} 1</h4>
              <p className="text-sm text-gray-600">{t('dashboard.reachedLevel1')}</p>
            </div>
            <div className="text-sm text-gray-500 mr-auto">
              {t('dashboard.now')}
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
